import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import './Guess.css';

const CongratulationsCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 50%, #d299c2 100%)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  textAlign: 'center',
  boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 10px 20px rgba(168, 237, 234, 0.2)',
  border: '3px solid rgba(255,255,255,0.6)',
  position: 'relative',
  overflow: 'hidden',
  backdropFilter: 'blur(10px)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.15), transparent)',
    animation: 'shimmer 4s infinite linear',
    transform: 'rotate(45deg)'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '20px',
    height: '20px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)',
    borderRadius: '50%',
    animation: 'sparkle 2s ease-in-out infinite alternate',
  },
  '@keyframes shimmer': {
    '0%': {
      transform: 'rotate(45deg) translateX(-100%)',
    },
    '100%': {
      transform: 'rotate(45deg) translateX(100%)',
    },
  },
  '@keyframes sparkle': {
    '0%': {
      opacity: 0.3,
      transform: 'scale(0.8)',
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1.2)',
    },
  },
}));

const ContinueButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: theme.spacing(2.5, 8),
  fontSize: '1.5rem',
  fontWeight: '800',
  textTransform: 'none',
  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 50%, #ff6b6b 100%)',
  backgroundSize: '200% 200%',
  color: 'white',
  border: '3px solid rgba(255,255,255,0.8)',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 15px 35px rgba(255, 107, 107, 0.4), 0 5px 15px rgba(0,0,0,0.1)',
  textShadow: '0 2px 8px rgba(0,0,0,0.6)',
  letterSpacing: '1.2px',
  minWidth: '320px',
  height: '70px',
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
    transition: 'left 0.6s ease-in-out',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '0',
    height: '0',
    background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    transition: 'width 0.6s ease, height 0.6s ease',
  },
  '&:hover': {
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: '0 20px 45px rgba(255, 107, 107, 0.6), 0 10px 25px rgba(255, 215, 0, 0.3)',
    backgroundPosition: '100% 0',
    borderColor: '#ffd700',
    filter: 'brightness(1.1)',
    '&::before': {
      left: '100%',
    },
    '&::after': {
      width: '300px',
      height: '300px',
    },
  },
  '&:active': {
    transform: 'translateY(-2px) scale(1.01)',
    boxShadow: '0 12px 25px rgba(255, 107, 107, 0.7)',
    transition: 'all 0.1s ease',
  },
  // Pulse animation
  animation: 'buttonPulse 3s ease-in-out infinite',
  '@keyframes buttonPulse': {
    '0%, 100%': {
      boxShadow: '0 15px 35px rgba(255, 107, 107, 0.4), 0 5px 15px rgba(0,0,0,0.1)',
    },
    '50%': {
      boxShadow: '0 20px 40px rgba(255, 107, 107, 0.6), 0 8px 20px rgba(0,0,0,0.2)',
    },
  },
}));

const NextLevel = (props) => {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <CongratulationsCard>
        <Box> {/* Removed Fade wrapper to fix the error */}
          {/* Celebration Header */}
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="h5"
              sx={{ 
                color: '#333',
                fontWeight: 'bold',
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              🎉 Congratulations! 🎉
            </Typography>
            
            <Typography 
              variant="body1"
              sx={{ 
                color: '#666',
                fontStyle: 'italic'
              }}
            >
              You guessed the correct number!
            </Typography>
          </Box>

          {/* Answer Display */}
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="body1"
              sx={{ 
                color: '#333',
                mb: 1
              }}
            >
              The answer was: 
              <span style={{ 
                color: '#e74c3c', 
                fontWeight: 'bold',
                marginLeft: '8px',
                padding: '2px 8px',
                background: 'rgba(255,255,255,0.5)',
                borderRadius: '6px'
              }}>
                {props.Answer}
              </span>
            </Typography>
          </Box>

          {/* Score Display */}
          <Box sx={{ mb: 2 }}>
            <Chip 
              label={`Total Score: ${props.score.toLocaleString()}`}
              icon={<TrophyIcon />}
              sx={{ 
                background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                color: '#333',
                fontSize: '1rem',
                fontWeight: 'bold',
                px: 2,
                py: 1,
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
              }}
            />
          </Box>

          {/* Next Level Info */}
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="body2"
              sx={{ 
                color: '#666',
                mb: 1,
                fontSize: '1rem'
              }}
            >
              Ready for the next challenge?
            </Typography>
          </Box>

          {/* Continue Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <ContinueButton
              onClick={props.newgame}
              endIcon={
                <ArrowIcon 
                  sx={{ 
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(5px)'
                    }
                  }} 
                />
              }
              sx={{
                '&:hover .MuiSvgIcon-root': {
                  transform: 'translateX(8px) scale(1.2)',
                }
              }}
            >
              🚀 Go To Level {props.level}
            </ContinueButton>
          </Box>
        </Box>
      </CongratulationsCard>
    </Box>
  );
}

export default NextLevel;